import { wave } from '@/api/config';
import { getV31Products } from '@/api/Products.js';
import CategoriesAisles from '@/components/CategoriesAisles/CategoriesAisles';
import PageLayout from '@/components/PageLayout';
import CategoryTitle from '@/components/CategoryTitle/CategoryTitle';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext.jsx';
import useWindowUnderThreshold from '@/hooks/useWindowUnderThreshold';
import { getProductInfo, selectPromotion, viewItemList, bannersAnalyticsFunction } from '@/utils/analytics.js';
import { transformCategoryAisles, replaceUndefinedWithNull } from '@/utils/categoryAisles';
import { getLayoutProps } from '@/utils/layout/layout';
import { enhanceProductWithSlug } from '@/utils/products/productSlugs.js';

import { getCmsHomepageLayout } from '@/api/cms';
import Head from 'next/head';
import React, { useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import Link from 'next/link';
import styles from '@/styles/Categories.module.css';
import CategoriesMenu from '@/components/CategoriesMenu/CategoriesMenu';
import cn from 'classnames';
import { WaveBreadCrumb, HomePageBanner } from '@desquared/wave-components-library';
import { getCategoriesFromFile } from '@/utils/getCategories';
import { useRouter } from 'next/router';
import { trimmCategories } from '@/utils/trimmCategories';
import { getOrFetchOrderLimitations } from '@/utils/orderLimitations';
import { sortByPosition, sortByIsFeaturedAndCategoryPosition } from '@/utils/category';
import useProductTags from '@/hooks/useProductTags';
import useBundleOffersSummary from '@/hooks/useBundleOffersSummary';
import { useTranslations } from 'next-intl';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import ScrollToTop from '@/components/ScrollToTop/ScrollToTop';
import DefaultProductImage from '../../../public/assets/wave-grocery/Empty-States-Default/DefaultProductImage.png';
import { usePersonalizedProductsIds } from '@/contexts/PersonalizedProductsIdsContext.jsx';
import { getVisibleCategories } from '@/api/Products.js';
import useDeliveryServices from '@/hooks/useDeliveryServices';
import { setBanner } from '@/utils/homePageCmsCall.js';
import { useIsRtl } from '@/contexts/DirectionContext.jsx';
import { useVisibleCategories } from '@/contexts/VisibleCategoriesContext';

const CATEGORIES_NUMBER_TO_PREFETCH = 5;
const MAX_CATEGORY_ITEMS_FOR_ANALYTICS = 5;
export default function Categories({
  allCategories,
  responsiveLogo,
  productsPerCategoryInitial,
  privateApiKey,
  featuredCategories,
  emptyStates,
  imageBanners,
}) {
  const observer = useRef(null);
  const isMobile = useWindowUnderThreshold(850);
  const { productTags } = useProductTags();
  const { bundleOffersSummary } = useBundleOffersSummary();
  const isRtl = useIsRtl();

  const router = useRouter();
  const { slug } = router.query;
  const t = useTranslations();
  const a = useTranslations('common.asides');

  const { locale, defaultLocale } = useRouter();
  const { getCollectionTypes, selectedService, selectedShop } = useContext(DeliveryServicesContext);
  const { shop } = useDeliveryServices();
  const defaultProdImg = emptyStates?.default_product_image?.url || DefaultProductImage.src;
  const { personalizedProductsIds } = usePersonalizedProductsIds();

  const transformedImageBanners = useMemo(() => {
    return setBanner(imageBanners);
  }, [imageBanners]);
  const { visibleCategories } = useVisibleCategories();

  const displayCategories = useMemo(() => {
    if (!visibleCategories) return allCategories;

    const myTree = [];
    for (const category of allCategories) {
      if (visibleCategories.includes(category._id))
        myTree.push({
          ...category,
          subcategories: category.subcategories.filter(el => (!!el._id ? visibleCategories.includes(el._id) : true)),
        });
    }

    return myTree;
  }, [visibleCategories, allCategories]);

  const breadCrumbItems = useMemo(
    () => [
      {
        title: t('categories.breadcrumbs.products'),
        link: '/categories',
      },
    ],
    [t]
  );

  const {
    data,
    fetchNextPage,
    isLoading: isLoadingAisles,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['product-aisles', router.locale, getCollectionTypes(), selectedService, shop],
    queryFn: async ({ pageParam }) => {
      return await Promise.all(
        allCategories
          .filter(
            (_, index) =>
              index >= pageParam * CATEGORIES_NUMBER_TO_PREFETCH &&
              index < (pageParam + 1) * CATEGORIES_NUMBER_TO_PREFETCH
          )
          .map(category =>
            getV31Products(
              { category: category._id, collectionTypes: getCollectionTypes(), pageSize: 10 },
              router.locale,
              privateApiKey
            )
          )
      );
    },
    getNextPageParam: (_, allPages) => {
      if (allPages.length >= Math.ceil(allCategories.length / CATEGORIES_NUMBER_TO_PREFETCH)) return undefined;

      return allPages.length;
    },
    initialPageParam: 0,
    initialData: { pages: [productsPerCategoryInitial], pageParams: [0] },
    staleTime: 0,
  });

  const { data: limitations } = useQuery({
    queryKey: ['limitations', getCollectionTypes()],
    queryFn: getOrFetchOrderLimitations,
  });

  useEffect(() => {
    if (!data) return;

    const lastPage = data.pages[data.pages.length - 1];

    // Send event for items from last load
    const analyticsItems = lastPage.flatMap(aisle =>
      aisle
        .filter((_, index) => index < MAX_CATEGORY_ITEMS_FOR_ANALYTICS)
        .map((product, index) => getProductInfo(product, { index: index + 1 }, 'categories'))
    );

    viewItemList(analyticsItems, {
      item_list_id: 'categories',
      item_list_name: 'categories',
    });
  }, [data]);

  const lastAisleRef = useCallback(
    node => {
      if (!node) return;
      if (isLoadingAisles || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      });
      observer.current.observe(node);
    },
    [isLoadingAisles, isFetchingNextPage, fetchNextPage]
  );

  return (
    <>
      <Head>
        <title>
          {process.env.displayName} | {t('categories.breadcrumbs.products')}
        </title>
        <meta name="description" content={`App for ${process.env.displayName} by Wave Grocery`} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href={responsiveLogo} />
      </Head>

      <main className={styles.mainContainer}>
        {isMobile ? (
          <div className={styles.breadCrumbContainer}>
            <WaveBreadCrumb
              LinkComponent={Link}
              items={breadCrumbItems}
              iconLink={`/${locale === defaultLocale ? '' : locale}`}
              isRtl={isRtl}
            />
          </div>
        ) : null}
        <div className={styles.categoriesWrapper}>
          <div className={styles.categoriesContainer}>
            <div className={styles.categoriesInnerContainer}>
              <CategoriesMenu
                categories={displayCategories}
                slug={slug}
                featuredAmmount={featuredCategories.length}
                isFirstLevel={true}
              />
            </div>
          </div>

          <div className={styles.productsContainer}>
            <div className={styles.breadCrumbContainer}>
              {isMobile ? null : (
                <WaveBreadCrumb
                  LinkComponent={Link}
                  items={breadCrumbItems}
                  iconLink={`/${locale === defaultLocale ? '' : locale}`}
                  isRtl={isRtl}
                />
              )}
            </div>
            {isMobile ? null : (
              <div className={styles.titleContainer}>
                <CategoryTitle title={t('categories.title')} subtitle={t('categories.subtitle')} />
              </div>
            )}
            <div className={styles.aislesContainer}>
              <HomePageBanner
                analyticsFunction={bannersAnalyticsFunction}
                isFixedWidth={true}
                images={transformedImageBanners}
                refreshTime={1000000}
                showPagination={true}
                isMobile={isMobile}
                classOption={cn(styles.banner)}
                LinkComponent={Link}
                onClick={index => {
                  selectPromotion({
                    creative_name: transformedImageBanners[index]?.altText ?? `categories_banner`,
                    creative_slot: transformedImageBanners[index]?.altText ?? 'categories_banner',
                    promotion_id: transformedImageBanners[index]?.altText ?? `categories_banner`,
                    promotion_name: transformedImageBanners[index]?.altText ?? `categories_banner`,
                  });
                }}
                isRtl={isRtl}
              />
              <CategoriesAisles
                aisles={
                  data
                    ? sortByIsFeaturedAndCategoryPosition(
                        transformCategoryAisles(
                          allCategories,
                          data.pages.flatMap(page => page.map(aisle => aisle.map(enhanceProductWithSlug))),
                          productTags,
                          bundleOffersSummary,
                          personalizedProductsIds,
                          a
                        )
                      )
                    : []
                }
                hasPadding={true}
                limitations={limitations}
                tags={productTags}
                bundleOffersSummary={bundleOffersSummary}
                lastAisleRef={lastAisleRef}
                defaultProductImage={defaultProdImg}
                isLoading={isLoadingAisles || isFetchingNextPage}
              />
            </div>
          </div>
        </div>
        <ScrollToTop />
      </main>
    </>
  );
}

Categories.getLayout = function getLayout(page) {
  return <PageLayout>{page}</PageLayout>;
};

export async function getStaticProps(context) {
  const layoutProps = await getLayoutProps({ headerProps: { selectedIndex: 0 } }, context.locale);
  const categoriesFromFile = await getCategoriesFromFile(context.locale);

  const messages = {
    common: (await import(`../../../prebuild-data/i18n/${context.locale}/common.json`)).default,
    categories: (await import(`../../../prebuild-data/i18n/${context.locale}/categories.json`)).default,
  };

  const { normalCategories, featuredCategories } = trimmCategories(
    categoriesFromFile,
    messages?.common?.categories_menu?.all_categories
  );
  const sortedFeaturedCategories = sortByPosition(featuredCategories);
  const sortedNormalCategories = sortByPosition(normalCategories);
  const allCategories = [...sortedFeaturedCategories, ...sortedNormalCategories];

  async function getV31ProductsWithAPIKey(resource) {
    const response = await wave.get('/v3.1/products', {
      headers: { 'x-api-key': process.env.PRIVATE_API_KEY, 'Accept-Language': context.locale },
      params: {
        available: true,
        pageNumber: 0,
        pageSize: 10,
        categorySearchType: 'ALL_LEVELS',
        ...resource,
      },
    });

    return response.data;
  }

  let productsPerCategory = [];
  try {
    productsPerCategory = await Promise.all(
      allCategories
        .filter((i, index) => index < CATEGORIES_NUMBER_TO_PREFETCH)
        .map(i => getV31ProductsWithAPIKey({ category: i._id }))
    );
  } catch (err) {
    console.error('>>>getStaticProps initialProducts error');
  }

  const mainCategory = layoutProps.categories.find(el => el.integratorId == 0 && el.friendlyId == 0);

  const { data: BannerData } = await getCmsHomepageLayout(mainCategory?.carousel_id, context.locale);
  let imageBanners = [];
  if (BannerData.included?.homepage_banner_web_item?.length > 0 && mainCategory?.carousel_id) {
    imageBanners = BannerData.included.homepage_banner_web_item;
  }

  const latestCategories = replaceUndefinedWithNull(allCategories);
  const latestFeaturedCategories = replaceUndefinedWithNull(featuredCategories);

  return {
    props: {
      ...layoutProps,
      allCategories: latestCategories.map(i => {
        return {
          ...i,
          icon: i?.icon || null,
          selectedIcon: i?.selectedIcon || null,
          top_banners: i?.top_banners || null,
          side_banners: i?.side_banners || null,
          position: i?.position || null,
          isFeatured: i?.isFeatured || null,
        };
      }),
      featuredCategories: latestFeaturedCategories.map(i => {
        return {
          ...i,
          icon: i?.icon || null,
          selectedIcon: i?.selectedIcon || null,
          top_banners: i?.top_banners || null,
          side_banners: i?.side_banners || null,
          position: i?.position || null,
          isFeatured: i?.isFeatured || null,
        };
      }),
      productsPerCategoryInitial: productsPerCategory,
      privateApiKey: process.env.NODE_ENV === 'development' ? process.env.PRIVATE_API_KEY : null,
      messages,
      imageBanners,
    },
  };
}
